import { IconNames } from '@blueprintjs/icons';
import { type BenefitType, type ReferentPriceType } from '@strata/constants';
import { CurrentBid } from '@strata/react-current-bid';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { BaseNotification, type NotificationBaseProps } from '@strata/react-notifications';
import { CountdownTimer } from '@strata/react-timers';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    useProductConfig,
} from 'services/ProductConfigHelpers';
import { AppRoutes } from 'services/routes';

export interface OutbidNotificationProps extends NotificationBaseProps {
    linkingId: string;
    segmentId: string;
    productServiceOid: string;
    productName: string;
    timestamp: string;
    small: boolean;
    additionalData: {
        auctionEnd: string;
        winningBidAmount: string;
        bidState: (typeof CurrentBid.states)[keyof typeof CurrentBid.states];
        strength: string;
        qty: number;
        benefit: BenefitType;
        referentPriceType: ReferentPriceType;
    };
}

export const OutbidNotification = ({
    linkingId,
    segmentId,
    productServiceOid,
    productName,
    timestamp,
    additionalData,
    onNotificationClick,
    onRemoveNotificationClick,
    status,
    small,
}: OutbidNotificationProps) => {
    const navigate = useNavigate();
    const productConfig = useProductConfig({
        segmentId,
        productServiceOid,
        benefit: additionalData?.benefit?.trim() as BenefitType,
    });
    return (
        <BaseNotification
            notificationTitle="Outbid"
            icon={IconNames.TAKE_ACTION}
            bodyText={`You were outbid on ${productName}.`}
            timestamp={timestamp}
            onNotificationClick={() => {
                onNotificationClick();
                navigate(AppRoutes.liveReferralDetails(linkingId));
            }}
            onRemoveNotificationClick={onRemoveNotificationClick}
            type={BaseNotification.Types.CAUTION}
            status={status}
            lapsed={dayjs().isAfter(additionalData.auctionEnd)}
            additionalData={
                <>
                    <div style={{ width: '25%' }}>
                        <DetailLabel label="Time Remaining" />
                        <CountdownTimer endTime={additionalData.auctionEnd} />
                    </div>
                    {!small && (
                        <>
                            <DetailLabelValuePair
                                style={{ width: '25%' }}
                                label="Current Winning Bid"
                                value={
                                    <CurrentBid
                                        bidAmount={additionalData.winningBidAmount}
                                        referentPriceType={additionalData.referentPriceType}
                                        bidState={additionalData.bidState}
                                    />
                                }
                            />
                            <DetailLabelValuePair
                                style={{ width: '25%' }}
                                label="Strength"
                                value={additionalData.strength}
                            />
                            <DetailLabelValuePair
                                style={{ width: '25%' }}
                                label="Estimated Quantity"
                                value={addQtyUnits(
                                    additionalData.qty,
                                    getEstDispensedQtyUnits(productConfig),
                                )}
                            />
                        </>
                    )}
                </>
            }
        />
    );
};
